import { register, run } from "./registry";
import { focusField } from "./keyboard";

/**
 * Journal Entry (docs/parity/accounts-gaps.md A-J5, A-J6): only the side chosen by Dr/Cr takes an amount, the footer shows the
 * difference as "x Dr" / "x Cr" and the balance of the ledger on the row being typed (CargoBooks Journal Voucher).
 */
register((root) => {
  root.querySelectorAll<HTMLFormElement>("form[data-journal-voucher]").forEach((form) => {
    if (form.dataset.jvBound === "1") return;
    form.dataset.jvBound = "1";
    const diff = form.querySelector<HTMLOutputElement>("#jv-diff");
    const balance = form.querySelector<HTMLOutputElement>("#jv-balance");
    const num = (el: HTMLInputElement | null) => parseFloat((el?.value || "0").replace(/,/g, "")) || 0;

    const narration = form.querySelector<HTMLInputElement>("#jv-narration");
    const rowTemplate = form.querySelector<HTMLTemplateElement>("#jv-lines template[data-kit-row]");
    const prepareNewRow = () => {
      const remarks = rowTemplate?.content.querySelector<HTMLInputElement>("[data-kit-col='remarks']");
      if (remarks) remarks.value = narration?.value ?? "";
    };
    // Only user edits broadcast; loading an existing voucher or copying rows retains each line's remarks.
    const broadcastNarration = () => {
      if (!narration) return;
      form.querySelectorAll<HTMLInputElement>("#jv-lines tbody [data-kit-col='remarks']")
        .forEach((remarks) => { remarks.value = narration.value; });
      prepareNewRow();
    };
    narration?.addEventListener("input", broadcastNarration);
    narration?.addEventListener("change", broadcastNarration);
    // Copy sets the header programmatically. Refresh the template immediately before any row-creation action.
    form.addEventListener("keydown", prepareNewRow, true);
    form.addEventListener("click", prepareNewRow, true);
    prepareNewRow();

    const showDiff = () => {
      if (!diff) return;
      let dr = 0, cr = 0;
      form.querySelectorAll<HTMLInputElement>("#jv-lines tbody [data-kit-col='debit']").forEach((c) => { dr += num(c); });
      form.querySelectorAll<HTMLInputElement>("#jv-lines tbody [data-kit-col='credit']").forEach((c) => { cr += num(c); });
      const d = Math.abs(dr - cr).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      diff.textContent = dr === cr ? "0.00" : `${d} ${dr > cr ? "Dr" : "Cr"}`;
      for (const [col, total] of [["debit", dr], ["credit", cr]] as const) {
        const output = form.querySelector<HTMLInputElement>(`[data-kit-sum-of='${col}']`);
        if (output) output.value = total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    };

    // The side not chosen is read-only and skipped by Enter; an amount typed on it moves to the chosen side.
    form.addEventListener("change", (e) => {
      const side = e.target;
      if (side instanceof HTMLSelectElement && side.hasAttribute("data-jv-side")) {
        const tr = side.closest("tr");
        const open = tr?.querySelector<HTMLInputElement>(`[data-kit-col='${side.value === "cr" ? "credit" : "debit"}']`);
        const shut = tr?.querySelector<HTMLInputElement>(`[data-kit-col='${side.value === "cr" ? "debit" : "credit"}']`);
        if (open && shut) {
          if (num(shut) !== 0 && num(open) === 0) open.value = shut.value;
          shut.value = "0.00";
          shut.readOnly = true; shut.tabIndex = -1;
          open.readOnly = false; open.removeAttribute("tabindex");
        }
      }
      showDiff();
    });
    form.addEventListener("input", showDiff);
    form.addEventListener("htmx:afterSwap", showDiff);
    // The kit's reset clears only ordered fields: the next voucher must not claim to be copied from the last one's source.
    form.addEventListener("kit:saved", () => {
      const copied = form.querySelector<HTMLInputElement>("#copied-from-id");
      if (copied) copied.value = "";
      document.getElementById("jv-details")?.replaceChildren();
      form.querySelector("#jv-cheques")?.replaceChildren();
      if (balance) balance.textContent = "";
      shownFor = "";
      showDiff();
    });

    let shownFor = "";
    const showBalance = async (tr: HTMLTableRowElement | null) => {
      if (!balance || !tr) return;
      const ledgerId = tr.querySelector<HTMLInputElement>("input[name$='.ledgerId']")?.value ?? "";
      const branchId = form.querySelector<HTMLSelectElement>("[name='branchId']")?.value ?? "";
      const key = `${ledgerId}|${branchId}`;
      if (key === shownFor) return;
      shownFor = key;
      if (!ledgerId) { balance.textContent = ""; return; }
      const r = await fetch(`?handler=Balance&ledgerId=${encodeURIComponent(ledgerId)}&branchId=${encodeURIComponent(branchId)}`);
      if (shownFor === key) balance.textContent = r.ok ? await r.text() : "";
    };
    // A-J8: a row's Transaction Type other than Normal takes its rows in a detail grid (a dialog outside this form, so its
    // fields are not posted); closing it writes the rows into the row's allocationsJson and the line amount = their sum.
    const details = document.getElementById("jv-details");
    const openSide = (tr: HTMLTableRowElement) =>
      tr.querySelector<HTMLInputElement>(`[data-kit-col='${tr.querySelector<HTMLSelectElement>("[data-jv-side]")?.value === "cr" ? "credit" : "debit"}']`);
    const dialogOf = (alloc: HTMLInputElement) =>
      alloc.dataset.jvKey ? details?.querySelector<HTMLDialogElement>(`dialog[data-jv-key='${alloc.dataset.jvKey}']`) ?? null : null;
    const writeBack = (tr: HTMLTableRowElement, dlg: HTMLDialogElement) => {
      const alloc = tr.querySelector<HTMLInputElement>("[data-jv-alloc]");
      if (!alloc) return;
      const rows = Array.from(dlg.querySelectorAll<HTMLTableRowElement>("tbody tr")).map((r) => ({
        targetId: r.querySelector<HTMLInputElement>("[data-kit-fill='id']")?.value || null,
        description: r.querySelector<HTMLInputElement>("[data-kit-col='description']")?.value.trim() || null,
        amount: num(r.querySelector<HTMLInputElement>("[data-kit-col='amount']")),
      })).filter((r) => r.amount !== 0 || r.targetId || r.description);
      alloc.value = rows.length ? JSON.stringify(rows) : "";
      const amount = openSide(tr);
      if (amount && rows.length) {
        amount.value = rows.reduce((s, r) => s + r.amount, 0).toFixed(2);
        amount.dispatchEvent(new Event("input", { bubbles: true }));
      }
    };
    const openDetail = async (tr: HTMLTableRowElement, type: string) => {
      const alloc = tr.querySelector<HTMLInputElement>("[data-jv-alloc]");
      if (!alloc || !details) return;
      let dlg = dialogOf(alloc);
      if (dlg && dlg.dataset.jvDetail !== type) { dlg.remove(); dlg = null; alloc.value = ""; }
      if (!dlg) {
        const branchId = form.querySelector<HTMLSelectElement>("[name='branchId']")?.value ?? "";
        const r = await fetch(`?handler=Detail&type=${encodeURIComponent(type)}&branchId=${encodeURIComponent(branchId)}`);
        if (!r.ok) return;
        const holder = document.createElement("div");
        holder.innerHTML = await r.text();
        dlg = holder.querySelector<HTMLDialogElement>("dialog");
        if (!dlg) return;
        alloc.dataset.jvKey = alloc.dataset.jvKey || crypto.randomUUID();
        dlg.dataset.jvKey = alloc.dataset.jvKey;
        details.append(dlg);
        run(dlg);
      }
      if (dlg.dataset.jvBound !== "1") {
        dlg.dataset.jvBound = "1";
        const shown = dlg;
        // Existing targets are resolved: traversal must not reload an open balance into the saved amount.
        shown.addEventListener("keydown", (e) => {
          const key = e.target;
          if (!(key instanceof HTMLInputElement) || !key.dataset.jvSavedNo || key.value !== key.dataset.jvSavedNo ||
              (e.key !== "Enter" && e.key !== "Tab") || e.shiftKey) return;
          e.preventDefault(); e.stopImmediatePropagation();
          const fields = Array.from(key.closest("tr")!.querySelectorAll<HTMLInputElement>("input:not([type='hidden']):not([readonly])"));
          focusField(fields[fields.indexOf(key) + 1]);
        }, true);
        shown.addEventListener("close", () => {
          const row = Array.from(form.querySelectorAll<HTMLInputElement>("[data-jv-alloc]")).find((a) => a.dataset.jvKey === shown.dataset.jvKey)?.closest("tr");
          if (!row) return;
          writeBack(row, shown);
          focusField(openSide(row) ?? undefined);
        });
      }
      dlg.showModal();
      focusField(dlg.querySelector<HTMLInputElement>("tbody tr input:not([type='hidden']):not([readonly])") ?? undefined);
    };
    form.addEventListener("keydown", (e) => {
      const type = e.target;
      if (e.key !== "Enter" || e.shiftKey || !(type instanceof HTMLSelectElement) || !type.hasAttribute("data-jv-type") || type.value === "normal") return;
      e.preventDefault(); e.stopPropagation();
      const tr = type.closest("tr");
      if (tr) void openDetail(tr, type.value);
    }, true);
    form.addEventListener("change", (e) => {
      const type = e.target;
      if (!(type instanceof HTMLSelectElement) || !type.hasAttribute("data-jv-type")) return;
      const alloc = type.closest("tr")?.querySelector<HTMLInputElement>("[data-jv-alloc]");
      if (alloc && dialogOf(alloc)?.dataset.jvDetail !== type.value) { dialogOf(alloc)?.remove(); alloc.value = ""; }
    });

    // A-P9: a row's ledger with post-dated cheques offers them in the footer; picking one fills that row's Remarks and amount
    // and the save clears the cheque. A cheque already picked is kept while other rows are typed.
    const cheques = form.querySelector<HTMLElement>("#jv-cheques");
    let chequeRow: HTMLTableRowElement | null = null;
    let chequeSeq = 0;
    form.addEventListener("kit:accepted", async (e) => {
      const cell = e.target as HTMLElement;
      const tr = cell.closest<HTMLTableRowElement>("#jv-lines tbody tr");
      if (!cheques || !tr || !cell.matches("[name$='.ledgerSearch']")) return;
      if (cheques.querySelector<HTMLSelectElement>("[data-pdc-pick]")?.value) return;
      const ledgerId = tr.querySelector<HTMLInputElement>("input[name$='.ledgerId']")?.value ?? "";
      const mine = ++chequeSeq;
      const r = await fetch(`?handler=Cheques&ledgerId=${encodeURIComponent(ledgerId)}`);
      const html = r.ok ? await r.text() : "";
      // A slow answer for an earlier row must not replace a later one, nor the list the user is on.
      if (mine !== chequeSeq || cheques.contains(document.activeElement)) return;
      cheques.innerHTML = html;
      chequeRow = tr;
      // The grid was left before the list arrived (nothing after the grid then): the cursor goes to the list.
      if (document.activeElement === document.body) focusField(cheques.querySelector<HTMLSelectElement>("[data-pdc-pick]") ?? undefined);
    });
    form.addEventListener("change", (e) => {
      const pick = e.target;
      if (!(pick instanceof HTMLSelectElement) || !pick.hasAttribute("data-pdc-pick") || !chequeRow) return;
      const fill = pick.selectedOptions[0]?.dataset.fill;
      if (!fill) return;
      const values = JSON.parse(fill) as Record<string, string>;
      const remarks = chequeRow.querySelector<HTMLInputElement>("[data-kit-col='remarks']");
      const amount = openSide(chequeRow);
      if (remarks) remarks.value = values.remarks ?? "";
      if (amount) { amount.value = values.amount ?? ""; amount.dispatchEvent(new Event("input", { bubbles: true })); }
    });

    showDiff();
    form.addEventListener("focusin", (e) => { void showBalance((e.target as HTMLElement).closest<HTMLTableRowElement>("#jv-lines tbody tr")); });
    form.addEventListener("kit:accepted", (e) => { void showBalance((e.target as HTMLElement).closest<HTMLTableRowElement>("#jv-lines tbody tr")); });
  });
});
